import { CodeContext } from '../types'
import authService from './auth'

// Backend API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
const AI_MODEL = import.meta.env.VITE_AI_MODEL || 'gpt-4o-mini'
const AI_TEMPERATURE = parseFloat(import.meta.env.VITE_AI_TEMPERATURE || '0.7')
const AI_MAX_TOKENS = parseInt(import.meta.env.VITE_AI_MAX_TOKENS || '500')

// Input sanitization function
const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    throw new Error('Invalid input: must be a non-empty string')
  }
  
  // Remove potentially dangerous characters and patterns
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets to prevent HTML injection
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol
    .substring(0, 4000) // Limit length
}

// Validate code context
const validateCodeContext = (context: CodeContext): void => {
  if (!context) {
    throw new Error('Code context is required')
  }
  
  if (context.transcribedThoughts && typeof context.transcribedThoughts !== 'string') {
    throw new Error('Transcribed thoughts must be a string')
  }
  
  if (context.currentCode && typeof context.currentCode !== 'string') {
    throw new Error('Current code must be a string')
  }
  
  if (context.problemDescription && typeof context.problemDescription !== 'string') {
    throw new Error('Problem description must be a string')
  }
  
  if (context.language && typeof context.language !== 'string') {
    throw new Error('Language must be a string')
  }
}

const SYSTEM_PROMPT = `You are a senior software engineer conducting a coding interview. You're providing real-time mentorship to a candidate.

Your role:
- Act like a real senior engineer - be conversational, direct, and authentic
- When the candidate asks a specific question via voice, ANSWER THEIR QUESTION directly
- When providing auto-feedback, analyze their current code and give constructive feedback
- Ask follow-up questions to guide their thinking
- Be encouraging but honest about areas for improvement
- Keep responses concise and actionable (max 2-3 sentences)
- Focus on code quality, best practices, and problem-solving approach

Guidelines:
- If they ask about a concept, explain it clearly with examples
- If they're stuck, provide hints rather than complete solutions
- If their code has issues, point them out constructively
- If they're on the right track, acknowledge their progress
- Use a conversational, mentor-like tone

Remember: You're helping them learn and grow as a developer.`

class AIService {
  private isSpeaking = false

  async getFeedback(context: CodeContext): Promise<string> {
    try {
      // Check authentication
      if (!authService.isAuthenticated()) {
        throw new Error('Authentication required. Please log in to continue.')
      }

      // Validate and sanitize input
      validateCodeContext(context)
      
      const sanitizedThoughts = context.transcribedThoughts ? 
        sanitizeInput(context.transcribedThoughts) : ''
      const sanitizedCode = context.currentCode ? 
        sanitizeInput(context.currentCode) : ''
      const sanitizedDescription = context.problemDescription ? 
        sanitizeInput(context.problemDescription) : ''

      const messages = [
        { role: 'system', content: SYSTEM_PROMPT },
        {
          role: 'user',
          content: `Problem: ${sanitizedDescription}

Current Code:
\`\`\`${context.language || 'javascript'}
${sanitizedCode}
\`\`\`

User's Question/Thought: ${sanitizedThoughts}

Previous Conversation Context:
${context.previousMessages?.map(msg => `${msg.type}: ${msg.content}`).join('\n') || 'None'}

Please provide ${context.conversationMode ? 'a conversational response to their question' : 'brief feedback on their code'}.`
        }
      ]

      // Get authentication token
      const token = await authService.getAuthToken()
      if (!token) {
        throw new Error('Authentication required. Please log in to continue.')
      }

      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages,
          model: AI_MODEL,
          temperature: AI_TEMPERATURE,
          max_tokens: AI_MAX_TOKENS,
        }),
      })

      if (response.status === 401 || response.status === 403) {
        // Authentication error - redirect to login
        authService.logout()
        throw new Error('Session expired. Please log in again.')
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('API Error:', response.status, errorData)
        throw new Error(`API request failed: ${response.status}`)
      }

      const data = await response.json()
      return data.choices?.[0]?.message?.content || 'Sorry, I encountered an error. Please try again.'
    } catch (error) {
      console.error('Error getting AI feedback:', error)
      return 'Sorry, I encountered an error processing your request. Please try again.'
    }
  }

  async speakFeedback(text: string, isUserQuestion: boolean = false): Promise<void> {
    if (this.isSpeaking) {
      console.log('AI is already speaking, skipping new speech request')
      return
    }

    try {
      // Check authentication
      if (!authService.isAuthenticated()) {
        console.warn('User not authenticated, skipping speech')
        return
      }

      // Sanitize text before sending to TTS
      const sanitizedText = sanitizeInput(text)
      
      this.isSpeaking = true

      // Get authentication token
      const token = await authService.getAuthToken()
      if (!token) {
        console.warn('No auth token available, using fallback TTS')
        this.fallbackTTS(sanitizedText)
        return
      }

      // Try OpenAI TTS first
      try {
        const response = await fetch(`${API_BASE_URL}/api/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            text: sanitizedText,
            voice: 'alloy',
            speed: 1.1,
          }),
        })

        if (response.status === 401 || response.status === 403) {
          // Authentication error - use fallback
          console.warn('Authentication failed for TTS, using fallback')
          this.fallbackTTS(sanitizedText)
          return
        }

        if (response.ok) {
          const audioBlob = await response.blob()
          const audioUrl = URL.createObjectURL(audioBlob)
          const audio = new Audio(audioUrl)
          
          audio.onended = () => {
            URL.revokeObjectURL(audioUrl)
            this.isSpeaking = false
          }
          
          audio.onerror = () => {
            URL.revokeObjectURL(audioUrl)
            this.isSpeaking = false
            this.fallbackTTS(sanitizedText)
          }
          
          await audio.play()
          return
        }
      } catch (error) {
        console.warn('OpenAI TTS failed, falling back to browser TTS:', error)
      }

      // Fallback to browser TTS
      this.fallbackTTS(sanitizedText)
    } catch (error) {
      console.error('Error in speech synthesis:', error)
      this.isSpeaking = false
    }
  }

  private fallbackTTS(text: string): void {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = 1.1
      utterance.pitch = 1.0
      utterance.volume = 0.9
      
      utterance.onend = () => {
        this.isSpeaking = false
      }
      
      utterance.onerror = () => {
        this.isSpeaking = false
      }
      
      speechSynthesis.speak(utterance)
    } else {
      console.warn('Speech synthesis not supported')
      this.isSpeaking = false
    }
  }

  stopSpeaking(): void {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }
    this.isSpeaking = false
  }

  isCurrentlySpeaking(): boolean {
    return this.isSpeaking
  }
}

export default new AIService() 